import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ASSET_LIBRARY, getAssetCategories, searchAssets } from "@mity-garden/asset-library";
function AssetCard({ asset, locale, onSelect, selected }) {
    const label = asset.labels[locale] ?? asset.labels["en"];
    const name = label?.name ?? asset.id;
    return (_jsxs("button", { onClick: () => onSelect(asset), "data-testid": `asset-card-${asset.id}`, title: label?.description ?? name, style: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 4,
            padding: "8px 4px",
            border: selected ? "2px solid #4caf50" : "2px solid transparent",
            borderRadius: 8,
            background: selected ? "#e8f5e9" : "transparent",
            cursor: "pointer",
            width: "100%",
            transition: "all 0.15s",
        }, onMouseEnter: (e) => {
            if (!selected)
                e.currentTarget.style.background = "#f5f5f5";
        }, onMouseLeave: (e) => {
            if (!selected)
                e.currentTarget.style.background = "transparent";
        }, children: [_jsx("div", { style: { width: 48, height: 48, flexShrink: 0 }, dangerouslySetInnerHTML: { __html: asset.thumbnail } }), _jsx("span", { style: {
                    fontSize: 10,
                    textAlign: "center",
                    lineHeight: 1.2,
                    color: "#424242",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 68,
                }, children: name })] }));
}
export function AssetLibraryPanel({ locale = "en", onAssetSelect, selectedAssetId = null, }) {
    const { t } = useTranslation("common");
    const [query, setQuery] = useState("");
    const [expandedCategories, setExpandedCategories] = useState(() => new Set(getAssetCategories().keys()));
    const filtered = useMemo(() => {
        if (query.trim().length === 0)
            return ASSET_LIBRARY;
        return searchAssets(query);
    }, [query, locale]);
    const byCategory = useMemo(() => {
        const map = new Map();
        for (const asset of filtered) {
            const cat = asset.category ?? asset.type;
            if (!map.has(cat))
                map.set(cat, []);
            map.get(cat).push(asset);
        }
        return map;
    }, [filtered]);
    function toggleCategory(cat) {
        setExpandedCategories((prev) => {
            const next = new Set(prev);
            if (next.has(cat))
                next.delete(cat);
            else
                next.add(cat);
            return next;
        });
    }
    const categories = Array.from(byCategory.keys());
    const allExpanded = categories.length > 0 && categories.every((c) => expandedCategories.has(c));
    function toggleAll() {
        if (allExpanded) {
            setExpandedCategories(new Set());
        }
        else {
            setExpandedCategories(new Set(categories));
        }
    }
    return (_jsxs("div", { "data-testid": "asset-library-panel", style: {
            display: "flex",
            flexDirection: "column",
            height: "100%",
            background: "#fff",
            borderRight: "1px solid #e0e0e0",
            width: 200,
            flexShrink: 0,
        }, children: [_jsxs("div", { style: {
                    padding: "10px 12px 8px",
                    borderBottom: "1px solid #e0e0e0",
                    background: "#f9fbe7",
                }, children: [_jsxs("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }, children: [_jsxs("div", { style: { fontWeight: 700, fontSize: 13, color: "#33691e" }, children: ["\uD83C\uDF31 ", t("assetLibrary.title")] }), _jsx("button", { onClick: toggleAll, "data-testid": "asset-library-toggle-all", title: allExpanded ? "Collapse all categories" : "Expand all categories", style: {
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    fontSize: 11,
                                    color: "#558b2f",
                                    padding: "2px 4px",
                                    borderRadius: 4,
                                    lineHeight: 1,
                                }, children: allExpanded ? "−All" : "+All" })] }), _jsx("input", { type: "search", placeholder: t("assetLibrary.search"), value: query, onChange: (e) => setQuery(e.target.value), "data-testid": "asset-search-input", style: {
                            width: "100%",
                            padding: "5px 8px",
                            fontSize: 12,
                            border: "1px solid #c8e6c9",
                            borderRadius: 6,
                            outline: "none",
                            boxSizing: "border-box",
                        } })] }), _jsx("div", { style: { flex: 1, overflowY: "auto" }, children: categories.length === 0 ? (_jsx("div", { style: { padding: 20, textAlign: "center", color: "#9e9e9e", fontSize: 13 }, children: "No assets found" })) : (categories.map((cat) => {
                    const assets = byCategory.get(cat) ?? [];
                    const expanded = expandedCategories.has(cat);
                    const label = t(`assetLibrary.categories.${cat}`, { defaultValue: cat });
                    return (_jsxs("div", { "data-testid": `asset-category-${cat}`, children: [_jsxs("button", { onClick: () => toggleCategory(cat), style: {
                                    width: "100%",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    padding: "6px 12px",
                                    background: "#f5f5f5",
                                    border: "none",
                                    borderBottom: "1px solid #e0e0e0",
                                    cursor: "pointer",
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: "#424242",
                                    textAlign: "left",
                                }, children: [_jsx("span", { children: label }), _jsxs("span", { style: { color: "#9e9e9e" }, children: [expanded ? "▾" : "▸", " ", assets.length] })] }), expanded && (_jsx("div", { style: {
                                    display: "grid",
                                    gridTemplateColumns: "repeat(2, 1fr)",
                                    gap: 4,
                                    padding: "6px 4px",
                                }, children: assets.map((asset) => (_jsx(AssetCard, { asset: asset, locale: locale, onSelect: (a) => onAssetSelect?.(a), selected: selectedAssetId === asset.id }, asset.id))) }))] }, cat));
                })) })] }));
}
//# sourceMappingURL=AssetLibraryPanel.js.map